/**
 * @file patiosData.js
 * @description Lógica de procesamiento de datos de patios, sitios y lecturas RFID
 * @module data/patiosData
 * 
 * Funcionalidades principales:
 * - Procesamiento de lecturas RFID (antenna_id: 1=patio, 4=movimiento)
 * - Filtrado y agrupación de vehículos por ubicación jerárquica:
 *   Estado → Sitio → Subsitio
 * - Estadísticas y resúmenes por marca, estado, ubicación
 * - Integración con API de patios (con fallback local)
 * - Clasificación de subsitios por tipo (taller, showroom, etc.)
 * 
 * IMPORTANTE: Sistema de antenas RFID
 * - antenna_id = "1" o null: Vehículo EN PATIO (entrada/estacionado)
 * - antenna_id = "4": Vehículo EN MOVIMIENTO (salida/tránsito)
 */

import { fetchAllSubsitiosFromPatio } from '../services/patiosService';
import { fetchAllSitiosByEstado } from '../services/patiosService';
import { fetchAllEstados } from '../services/patiosService';

/**
 * =================== FUNCIONES DE SUBSITIOS ===================
 */

/**
 * Obtiene vehículos de un subsitio específico
 * Filtra por subsitio, sitio padre y estado
 * 
 * @function getVehiculosBySubSitio
 * @param {Array} vehiculos - Array de vehículos
 * @param {string} subSitioNombre - Nombre del subsitio
 * @param {string} sitioNombre - Nombre del sitio padre
 * @param {string|null} [estadoNombre=null] - Estado (opcional)
 * @returns {Array} Vehículos filtrados del subsitio
 */
export const getVehiculosBySubSitio = (vehiculos, subSitioNombre, sitioNombre, estadoNombre = null) => {
  return vehiculos.filter(vehiculo => {
    if (!vehiculo.lectura_rfid) return false;
    const matchSubSitio = vehiculo.lectura_rfid.subpatio_nombre === subSitioNombre ||
      (subSitioNombre === 'Sin subsitio' && !vehiculo.lectura_rfid.subpatio_nombre);
    const matchSitio = vehiculo.lectura_rfid.patio_nombre === sitioNombre;
    const matchEstado = estadoNombre
      ? vehiculo.lectura_rfid.patio_estado === estadoNombre
      : true;
    return matchSubSitio && matchSitio && matchEstado;
  });
};

/**
 * Determina el tipo de un subsitio basándose en su nombre
 * Usa palabras clave para clasificar automáticamente
 * 
 * @function getSubSitioType
 * @param {string} subSitioNombre - Nombre del subsitio
 * @returns {string} Tipo: 'transito'|'patio'|'taller'|'showroom'|'entrega'|'almacen'|'general'
 */
export const getSubSitioType = (subSitioNombre) => {
  if (!subSitioNombre) return 'general';

  const nombre = subSitioNombre.toLowerCase();

  if (nombre.includes('transito') || nombre.includes('tránsito')) return 'transito';
  if (nombre.includes('patio')) return 'patio';
  if (nombre.includes('taller') || nombre.includes('servicio')) return 'taller';
  if (nombre.includes('showroom') || nombre.includes('exhibicion') || nombre.includes('exhibición')) return 'showroom';
  if (nombre.includes('entrega')) return 'entrega';
  if (nombre.includes('bodega') || nombre.includes('almacen') || nombre.includes('almacén')) return 'almacen';

  return 'general';
};

/**
 * Obtiene clases CSS de Tailwind para un tipo de subsitio
 */
export const getSubSitioColor = (tipo) => {
  const colorMap = {
    'transito': 'bg-yellow-100 text-yellow-800',
    'patio': 'bg-blue-100 text-blue-800',
    'taller': 'bg-purple-100 text-purple-800',
    'showroom': 'bg-green-100 text-green-800',
    'entrega': 'bg-indigo-100 text-indigo-800',
    'almacen': 'bg-gray-100 text-gray-800',
    'general': 'bg-slate-100 text-slate-800'
  };
  return colorMap[tipo] || 'bg-gray-100 text-gray-800';
};

/**
 * =================== FUNCIONES DE PATIOS ===================
 */

/**
 * Genera estructura de patios desde vehículos
 * Agrupa por BID y calcula estadísticas
 */
export const getPatinosFromVehiculos = (vehiculos) => {
  const patiosMap = new Map();

  vehiculos.forEach(vehiculo => {
    const patioName = vehiculo.BID;
    if (!patioName) return;

    if (!patiosMap.has(patioName)) {
      patiosMap.set(patioName, {
        name: patioName,
        autos: 0,
        estado: vehiculo.Estado,
        categoria: null,
        marcas: {},
        subPatios: new Set()
      });
    }

    const patio = patiosMap.get(patioName);
    patio.autos += 1;

    if (vehiculo.lectura_rfid?.patio_categoria && !patio.categoria) {
      patio.categoria = vehiculo.lectura_rfid.patio_categoria;
    }

    const marca = vehiculo.Marca;
    if (!patio.marcas[marca]) {
      patio.marcas[marca] = 0;
    }
    patio.marcas[marca] += 1;

    if (vehiculo.lectura_rfid?.subpatio_nombre) {
      patio.subPatios.add(vehiculo.lectura_rfid.subpatio_nombre);
    }
  });

  return Array.from(patiosMap.values()).map(patio => ({
    ...patio,
    categoria: patio.categoria || 'PATIO',
    subPatios: Array.from(patio.subPatios)
  }));
};

export const getPatioCategoryFromVehiculos = (vehiculos, patioName) => {
  const vehiculoConRFID = vehiculos.find(v =>
    v.lectura_rfid?.patio_nombre === patioName &&
    v.lectura_rfid?.patio_categoria
  );
  return vehiculoConRFID?.lectura_rfid?.patio_categoria || 'PATIO';
};

export const getAllPatioCategories = (vehiculos) => {
  const categoriesSet = new Set();

  vehiculos.forEach(vehiculo => {
    if (vehiculo.lectura_rfid?.patio_categoria) {
      categoriesSet.add(vehiculo.lectura_rfid.patio_categoria);
    }
  });

  return Array.from(categoriesSet).sort();
};

export const getPatioCategoryColor = (categoria) => {
  const colorMap = {
    'PATIO': 'bg-blue-100 text-blue-800',
    'SUCURSAL': 'bg-green-100 text-green-800',
    'VARIOS': 'bg-yellow-100 text-yellow-800',
    'TALLER': 'bg-purple-100 text-purple-800'
  };
  return colorMap[categoria?.toUpperCase()] || 'bg-gray-100 text-gray-800';
};

/**
 * Obtiene vehículos con estatus 7 - Entregado
 * Para uso en "Control de Facturadas No Entregadas"
 * 
 * @function getVehiculosEntregados
 * @param {Array} vehiculos - Array de vehículos
 * @returns {Array} Vehículos con estatus 7 (Entregado)
 */
export const getVehiculosEntregados = (vehiculos) => getVehiculosByStatus(vehiculos, 7);

/**
 * =================== CONSTANTES DE MARCAS ===================
 */

export const marcasColors = {
  'FORD': '#1D4ED8',
  'LINCOLN': '#059669',
  'GWM': '#D61117',
  'MAZDA': '#1E8ECC',
};

export const marcasLogos = {
  'FORD': '/logos/ford.png',
  'LINCOLN': '/logos/lincoln.png',
  'GWM': '/logos/gwm.png',
  'MAZDA': '/logos/mazda.png',
};

/**
 * =================== FUNCIONES DE ESTATUS ===================
 */

export const getStatusName = (statusId) => {
  const statusMap = {
    1: 'Inventario',
    2: 'Reservado',
    3: 'Unidades por arribar',
    4: 'Vendido',
    5: 'Cancelado',
    6: 'Facturado no entregado',
    7: 'Unidades en Salida'
  };
  return statusMap[statusId] || 'Desconocido';
};

export const getStatusColor = (statusId) => {
  const colorMap = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-yellow-100 text-yellow-800',
    3: 'bg-blue-100 text-blue-800',
    4: 'bg-purple-100 text-purple-800',
    5: 'bg-red-100 text-red-800',
    6: 'bg-orange-100 text-orange-800',
    7: 'bg-red-800 text-white'
  };
  return colorMap[statusId] || 'bg-gray-100 text-gray-800';
};

export const getVehiculosByStatus = (vehiculos, status) => {
  return vehiculos.filter(vehiculo => vehiculo.estatus === status);
};

export const getVehiculosInventario = (vehiculos) => getVehiculosByStatus(vehiculos, 1);
export const getVehiculosReservados = (vehiculos) => getVehiculosByStatus(vehiculos, 2);
export const getVehiculosEnTransito = (vehiculos) => getVehiculosByStatus(vehiculos, 3);
export const getVehiculosVendidos = (vehiculos) => getVehiculosByStatus(vehiculos, 4);
export const getVehiculosFacturadosNoEntregados = (vehiculos) => getVehiculosByStatus(vehiculos, 6);

export const getAllStatusFromVehiculos = (vehiculos) => {
  const statusSet = new Set(vehiculos.map(v => v.estatus).filter(s => s !== null && s !== undefined));
  return Array.from(statusSet).map(statusId => ({
    id: statusId,
    name: getStatusName(statusId),
    color: getStatusColor(statusId),
    count: vehiculos.filter(v => v.estatus === statusId).length
  }));
};

/**
 * =================== FUNCIONES DE RFID ===================
 * Sistema de detección y ubicación basado en lecturas RFID
 */

export const hasRFIDDetected = (vehiculo) => {
  return vehiculo.detectado_rfid === true && vehiculo.lectura_rfid !== null;
};

export const getVehiculosConRFID = (vehiculos) => {
  return vehiculos.filter(vehiculo => hasRFIDDetected(vehiculo));
};

export const getVehiculosSinRFID = (vehiculos) => {
  return vehiculos.filter(vehiculo => !hasRFIDDetected(vehiculo));
};

export const getVehiculosConLecturaRFID = (vehiculos) => {
  return vehiculos.filter(vehiculo =>
    vehiculo.lectura_rfid &&
    vehiculo.lectura_rfid !== null &&
    vehiculo.lectura_rfid.patio_nombre
  );
};

/**
 * Determina si un vehículo está físicamente en patio según modo_antena.
 *
 * - pistola_manual           → siempre en patio (colocado manualmente)
 * - antena + entrada/hibrida → en patio
 * - antena + salida/salida_definitiva → en movimiento
 * - antena + null            → registro sin modo_antena (datos anteriores), no se clasifica
 */
export const vehiculoEstaEnPatio = (vehiculo) => {
  const { tipo_lectura, modo_antena } = vehiculo.lectura_rfid || {};
  if (tipo_lectura === 'pistola_manual') return true;
  return modo_antena === 'entrada' || modo_antena === 'hibrida';
};

export const vehiculoEstaEnMovimiento = (vehiculo) => {
  const { tipo_lectura, modo_antena } = vehiculo.lectura_rfid || {};
  if (tipo_lectura === 'pistola_manual') return false;
  return modo_antena === 'salida' || modo_antena === 'salida_definitiva';
};

export const getVehiculosEnMovimiento = (vehiculos) => {
  return vehiculos.filter(vehiculo =>
    vehiculo.lectura_rfid && vehiculoEstaEnMovimiento(vehiculo)
  );
};

export const getVehiculosEnPatio = (vehiculos) => {
  return vehiculos.filter(vehiculo =>
    vehiculo.lectura_rfid && vehiculoEstaEnPatio(vehiculo)
  );
};

/**
 * Vehículos con lectura RFID válida que están actualmente en patio.
 */
export const getVehiculosConLecturaRFIDEnPatio = (vehiculos) => {
  return vehiculos.filter(vehiculo =>
    vehiculo.lectura_rfid &&
    vehiculo.lectura_rfid.patio_nombre &&
    vehiculoEstaEnPatio(vehiculo)
  );
};

/**
 * =================== FUNCIONES DE AGRUPACIÓN ===================
 */

export const groupVehiculosByMarca = (vehiculos) => {
  const grouped = {};

  vehiculos.forEach(vehiculo => {
    const marca = vehiculo.Marca;
    if (!marca) return;

    if (!grouped[marca]) {
      grouped[marca] = [];
    }
    grouped[marca].push(vehiculo);
  });

  return grouped;
};

export const countVehiculosByMarca = (vehiculos) => {
  const grouped = groupVehiculosByMarca(vehiculos);
  const count = {};

  Object.entries(grouped).forEach(([marca, vehiculosArray]) => {
    count[marca] = vehiculosArray.length;
  });

  return count;
};

export const groupVehiculosByBID = (vehiculos) => {
  const grouped = {};

  vehiculos.forEach(vehiculo => {
    const bid = vehiculo.BID;
    if (!bid) return;

    if (!grouped[bid]) {
      grouped[bid] = [];
    }
    grouped[bid].push(vehiculo);
  });

  return grouped;
};

export const groupVehiculosByEstado = (vehiculos) => {
  const grouped = {};

  vehiculos.forEach(vehiculo => {
    const estado = vehiculo.Estado;
    if (!estado) return;

    if (!grouped[estado]) {
      grouped[estado] = [];
    }
    grouped[estado].push(vehiculo);
  });

  return grouped;
};

/**
 * =================== FUNCIONES DE ESTADÍSTICAS ===================
 */

export const getEstadisticasGenerales = (vehiculos) => {
  const totalVehiculos = vehiculos.length;
  const conRFID = getVehiculosConRFID(vehiculos).length;
  const sinRFID = getVehiculosSinRFID(vehiculos).length;
  const enPatio = getVehiculosEnPatio(vehiculos).length;
  const enMovimiento = getVehiculosEnMovimiento(vehiculos).length;
  const porMarca = countVehiculosByMarca(vehiculos);
  const porEstatus = {};

  getAllStatusFromVehiculos(vehiculos).forEach(status => {
    porEstatus[status.name] = status.count;
  });

  return {
    totalVehiculos,
    conRFID,
    sinRFID,
    enPatio,
    enMovimiento,
    porcentajeRFID: totalVehiculos > 0 ? ((conRFID / totalVehiculos) * 100).toFixed(1) : '0',
    porMarca,
    porEstatus
  };
};

export const getEstadisticasPorMarca = (vehiculos, marca) => {
  const vehiculosMarca = vehiculos.filter(v => v.Marca === marca);
  const total = vehiculosMarca.length;
  const conRFID = getVehiculosConRFID(vehiculosMarca).length;
  const sinRFID = total - conRFID;

  const porEstatus = {};
  getAllStatusFromVehiculos(vehiculosMarca).forEach(status => {
    porEstatus[status.name] = status.count;
  });

  return {
    marca,
    total,
    conRFID,
    sinRFID,
    porcentajeRFID: total > 0 ? ((conRFID / total) * 100).toFixed(1) : '0',
    porEstatus
  };
};

export const getResumenUbicaciones = (vehiculos) => {
  const ubicaciones = {
    enPatio: { count: 0, vehiculos: [], porcentaje: 0 },
    enMovimiento: { count: 0, vehiculos: [], porcentaje: 0 },
    sinLectura: { count: 0, vehiculos: [], porcentaje: 0 }
  };

  vehiculos.forEach(vehiculo => {
    if (!vehiculo.lectura_rfid) {
      ubicaciones.sinLectura.count++;
      ubicaciones.sinLectura.vehiculos.push(vehiculo);
    } else if (vehiculoEstaEnPatio(vehiculo)) {
      ubicaciones.enPatio.count++;
      ubicaciones.enPatio.vehiculos.push(vehiculo);
    } else if (vehiculoEstaEnMovimiento(vehiculo)) {
      ubicaciones.enMovimiento.count++;
      ubicaciones.enMovimiento.vehiculos.push(vehiculo);
    }
  });

  const total = vehiculos.length;
  if (total > 0) {
    ubicaciones.enPatio.porcentaje = ((ubicaciones.enPatio.count / total) * 100).toFixed(1);
    ubicaciones.enMovimiento.porcentaje = ((ubicaciones.enMovimiento.count / total) * 100).toFixed(1);
    ubicaciones.sinLectura.porcentaje = ((ubicaciones.sinLectura.count / total) * 100).toFixed(1);
  }

  return ubicaciones;
};

export const getEstadoVehiculoPorAntenna = (vehiculo) => {
  if (!vehiculo.lectura_rfid) return 'sin_lectura';
  if (vehiculoEstaEnPatio(vehiculo)) return 'en_patio';
  if (vehiculoEstaEnMovimiento(vehiculo)) return 'en_movimiento';
  return 'estado_desconocido';
};

export const getDescripcionEstadoAntenna = (estado) => {
  const descripciones = {
    'en_patio': 'En patio (última lectura: entrada)',
    'en_movimiento': 'En movimiento (última lectura: salida)',
    'sin_lectura': 'Sin lectura RFID',
    'estado_desconocido': 'Estado desconocido'
  };
  return descripciones[estado] || 'Estado no definido';
};

export const getColorEstadoAntenna = (estado) => {
  const colores = {
    'en_patio': 'bg-green-100 text-green-800',
    'en_movimiento': 'bg-orange-100 text-orange-800',
    'sin_lectura': 'bg-gray-100 text-gray-800',
    'estado_desconocido': 'bg-red-100 text-red-800'
  };
  return colores[estado] || 'bg-gray-100 text-gray-800';
};

/**
 * =================== FUNCIONES DE ESTADOS Y SITIOS RFID ===================
 * Funciones que integran con la API de patios
 */

export const getEstadosFromVehiculos = async (vehiculos) => {
  try {
    const resultadoAPI = await fetchAllEstados(vehiculos);

    if (resultadoAPI.success && resultadoAPI.data.length > 0) {
      return resultadoAPI.data;
    }

    const vehiculosEnPatio = getVehiculosConLecturaRFIDEnPatio(vehiculos);
    const estadosMap = new Map();

    vehiculosEnPatio.forEach(vehiculo => {
      const estado = vehiculo.lectura_rfid?.patio_estado || vehiculo.Estado;
      if (!estado) return;

      if (!estadosMap.has(estado)) {
        estadosMap.set(estado, {
          nombre: estado,
          totalVehiculos: 0,
          sitios: new Set(),
          marcas: {},
          vehiculos: [],
          estaVacio: true
        });
      }

      const estadoData = estadosMap.get(estado);
      estadoData.totalVehiculos += 1;
      estadoData.vehiculos.push(vehiculo);
      estadoData.estaVacio = false;

      const sitio = vehiculo.lectura_rfid?.patio_nombre || vehiculo.BID;
      if (sitio) estadoData.sitios.add(sitio);

      const marca = vehiculo.Marca || 'DESCONOCIDA';
      estadoData.marcas[marca] = (estadoData.marcas[marca] || 0) + 1;
    });

    const estadosEsperados = ["YUCATÁN", "CAMPECHE", "QUINTANA ROO"];
    estadosEsperados.forEach(nombre => {
      if (!estadosMap.has(nombre)) {
        estadosMap.set(nombre, {
          nombre,
          totalVehiculos: 0,
          sitios: [],
          marcas: {},
          vehiculos: [],
          estaVacio: true
        });
      }
    });

    const estadosArray = Array.from(estadosMap.values()).map(estado => ({
      ...estado,
      totalSitios: estado.sitios?.size || 0,
      sitios: Array.isArray(estado.sitios) ? estado.sitios : Array.from(estado.sitios),
      estaVacio: estado.totalVehiculos === 0
    }));

    estadosArray.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return estadosArray;
  } catch (error) {
    console.error('Error al obtener estados:', error);
    return [];
  }
};

export const getSitiosByEstado = async (vehiculos, estadoNombre) => {
  try {
    const resultadoAPI = await fetchAllSitiosByEstado(estadoNombre, vehiculos);

    if (resultadoAPI.success && resultadoAPI.data.length > 0) {
      return resultadoAPI.data;
    }

    const vehiculosEnPatio = getVehiculosConLecturaRFIDEnPatio(vehiculos);
    const vehiculosDelEstado = vehiculosEnPatio.filter(
      v => v.lectura_rfid.patio_estado === estadoNombre
    );

    const sitiosMap = new Map();

    vehiculosDelEstado.forEach(vehiculo => {
      const sitioNombre = vehiculo.lectura_rfid.patio_nombre;
      const categoria = vehiculo.lectura_rfid.patio_categoria;

      if (!sitioNombre) return;

      if (!sitiosMap.has(sitioNombre)) {
        sitiosMap.set(sitioNombre, {
          nombre: sitioNombre,
          categoria: categoria || 'PATIO',
          totalVehiculos: 0,
          marcas: {},
          vehiculos: [],
          estado: estadoNombre,
          estaVacio: false
        });
      }

      const sitioData = sitiosMap.get(sitioNombre);
      sitioData.totalVehiculos += 1;
      sitioData.vehiculos.push(vehiculo);

      const marca = vehiculo.Marca;
      sitioData.marcas[marca] = (sitioData.marcas[marca] || 0) + 1;
    });

    const sitiosArray = Array.from(sitiosMap.values());
    sitiosArray.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return sitiosArray;
  } catch (error) {
    console.error('Error al obtener sitios:', error);
    return [];
  }
};

export const getSubSitiosBySitio = async (vehiculos, sitioNombre, estadoNombre = null) => {
  try {
    const vehiculosDelSitio = vehiculos.filter(vehiculo => {
      if (!vehiculo.lectura_rfid) return false;
      const matchSitio = vehiculo.lectura_rfid.patio_nombre === sitioNombre;
      const matchEstado = estadoNombre
        ? vehiculo.lectura_rfid.patio_estado === estadoNombre
        : true;
      return matchSitio && matchEstado;
    });

    const resultadoAPI = await fetchAllSubsitiosFromPatio(sitioNombre, vehiculos);

    if (resultadoAPI.success && resultadoAPI.data.length > 0) {
      const subsitiosRecalculados = resultadoAPI.data.map(subsitio => {
        const vehiculosDelSubsitio = vehiculosDelSitio.filter(vehiculo => {
          const matchSubSitio = vehiculo.lectura_rfid.subpatio_nombre === subsitio.nombre ||
            (subsitio.nombre === 'Sin subsitio' && !vehiculo.lectura_rfid.subpatio_nombre);
          return matchSubSitio;
        });

        const marcasRecalculadas = {};
        vehiculosDelSubsitio.forEach(vehiculo => {
          const marca = vehiculo.Marca;
          marcasRecalculadas[marca] = (marcasRecalculadas[marca] || 0) + 1;
        });

        return {
          ...subsitio,
          totalVehiculos: vehiculosDelSubsitio.length,
          marcas: marcasRecalculadas,
          vehiculos: vehiculosDelSubsitio,
          estaVacio: vehiculosDelSubsitio.length === 0
        };
      });

      return subsitiosRecalculados;
    }

    const subSitiosMap = new Map();

    vehiculosDelSitio.forEach(vehiculo => {
      const subSitioNombre = vehiculo.lectura_rfid.subpatio_nombre || 'Sin subsitio';

      if (!subSitiosMap.has(subSitioNombre)) {
        subSitiosMap.set(subSitioNombre, {
          nombre: subSitioNombre,
          sitio: sitioNombre,
          estado: estadoNombre,
          totalVehiculos: 0,
          marcas: {},
          vehiculos: [],
          estaVacio: false
        });
      }

      const subSitioData = subSitiosMap.get(subSitioNombre);
      subSitioData.totalVehiculos += 1;
      subSitioData.vehiculos.push(vehiculo);

      const marca = vehiculo.Marca;
      subSitioData.marcas[marca] = (subSitioData.marcas[marca] || 0) + 1;
    });

    const subSitiosArray = Array.from(subSitiosMap.values());
    subSitiosArray.sort((a, b) => {
      if (a.estaVacio && !b.estaVacio) return 1;
      if (!a.estaVacio && b.estaVacio) return -1;
      return a.nombre.localeCompare(b.nombre);
    });

    return subSitiosArray;
  } catch (error) {
    console.error('Error al obtener subsitios:', error);
    return [];
  }
};

export const getVehiculosBySitio = (vehiculos, sitioNombre, estadoNombre = null) => {
  return vehiculos.filter(vehiculo => {
    if (!vehiculo.lectura_rfid) return false;
    const matchSitio = vehiculo.lectura_rfid.patio_nombre === sitioNombre;
    const matchEstado = estadoNombre
      ? vehiculo.lectura_rfid.patio_estado === estadoNombre
      : true;
    return matchSitio && matchEstado;
  });
};

/**
 * =================== FUNCIONES DE UTILIDAD ===================
 */

export const getSitioCategoryColor = (categoria) => {
  const colorMap = {
    'PATIO': 'bg-blue-100 text-blue-800',
    'SUCURSAL': 'bg-green-100 text-green-800',
    'VARIOS': 'bg-yellow-100 text-yellow-800',
    'TALLER': 'bg-purple-100 text-purple-800'
  };
  return colorMap[categoria?.toUpperCase()] || 'bg-gray-100 text-gray-800';
};

export const getEstadoIcon = (estado) => {
  const iconMap = {
    'YUCATAN': '🏛️',
    'YUCATÁN': '🏛️',
    'CAMPECHE': '🌊',
    'QUINTANA ROO': '🏖️'
  };
  return iconMap[estado?.toUpperCase()] || '📍';
};

export const getAllMarcasFromVehiculos = (vehiculos) => {
  const marcasSet = new Set(vehiculos.map(v => v.Marca).filter(m => m));
  return Array.from(marcasSet).sort();
};

export const getAllBIDsFromVehiculos = (vehiculos) => {
  const bidsSet = new Set(vehiculos.map(v => v.BID).filter(b => b));
  return Array.from(bidsSet).sort();
};

export const getAllEstadosFromVehiculos = (vehiculos) => {
  const estadosSet = new Set(vehiculos.map(v => v.Estado).filter(e => e));
  return Array.from(estadosSet).sort();
};
